import {
  BookOpen,
  CheckSquare,
  FileText,
  Link as LinkIcon,
  MessageSquare,
  Paperclip,
  Sparkles,
} from 'lucide-react';
import type React from 'react';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

export interface FeatureBarItem {
  value: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  hasAttention?: boolean;
}

export interface FeatureOption {
  value: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const FEATURE_OPTIONS: FeatureOption[] = [
  {
    value: 'chat',
    title: 'AI',
    description: 'Ask questions and work with the page you have open',
    icon: Sparkles,
  },
  {
    value: 'notes',
    title: 'Notes',
    description: 'Private notes attached to this page anchor',
    icon: FileText,
  },
  {
    value: 'docs',
    title: 'Docs',
    description: 'Anchor documentation & reference guides',
    icon: BookOpen,
  },
  {
    value: 'tasks',
    title: 'Tasks',
    description: 'Actionable tasks & to-dos for this anchor',
    icon: CheckSquare,
  },
  {
    value: 'threads',
    title: 'Comments',
    description: 'Discussions & comments attached here',
    icon: MessageSquare,
  },
  {
    value: 'links',
    title: 'Links',
    description: 'Saved links & resource URLs',
    icon: LinkIcon,
  },
  {
    value: 'files',
    title: 'Files',
    description: 'Uploaded attachments & media files',
    icon: Paperclip,
  },
];

export const FEATURE_TITLE_MAP: Record<string, string> = {
  tasks: 'Tasks',
  notes: 'Notes',
  threads: 'Comments',
  chat: 'AI Copilot',
  links: 'Links',
  docs: 'Docs',
  files: 'Files',
};

export interface FeatureIconBarProps {
  anchorId?: number;
  activeFeature: string;
  onSelectFeature: (feature: string) => void;
  className?: string;
}

export function FeatureIconBar({
  anchorId,
  activeFeature,
  onSelectFeature,
  className,
}: FeatureIconBarProps) {
  const { data: flags } = trpc.featureFlags.list.useQuery();
  const { data: metrics } = trpc.anchors.getPageMetrics.useQuery(
    { anchorId: anchorId ?? 0 },
    { enabled: Boolean(anchorId) }
  );

  const myTasks = metrics?.tasks.mine ?? 0;
  const totalTasks = metrics?.tasks.total ?? 0;
  const hasOverdueTasks = metrics?.tasks.hasOverdue ?? false;

  const featuresWithMetrics: FeatureBarItem[] = [
    {
      value: 'tasks',
      title: 'Tasks',
      description: metrics
        ? `${totalTasks} active task${totalTasks === 1 ? '' : 's'}${myTasks > 0 ? ` (${myTasks} for you)` : ''}`
        : 'Actionable tasks & to-dos for this anchor',
      icon: CheckSquare,
      badge: myTasks > 0 ? myTasks : totalTasks > 0 ? totalTasks : undefined,
      hasAttention: hasOverdueTasks || myTasks > 0,
    },
    {
      value: 'notes',
      title: 'Notes',
      description:
        metrics && metrics.notes > 0
          ? `${metrics.notes} note${metrics.notes === 1 ? '' : 's'} on this page`
          : 'Private notes attached to this page anchor',
      icon: FileText,
      badge: metrics?.notes && metrics.notes > 0 ? metrics.notes : undefined,
    },
    {
      value: 'threads',
      title: 'Comments',
      description:
        metrics && metrics.comments > 0
          ? `${metrics.comments} team comment${metrics.comments === 1 ? '' : 's'}`
          : 'Team comments & discussion on this page',
      icon: MessageSquare,
      badge: metrics?.comments && metrics.comments > 0 ? metrics.comments : undefined,
    },
    {
      value: 'chat',
      title: 'AI',
      description: metrics?.ai.isIndexed
        ? 'Page context indexed in memory'
        : 'Contextual intelligence and in-page assistance',
      icon: Sparkles,
      badge: metrics?.ai.isIndexed ? 'AI' : undefined,
      hasAttention: Boolean(metrics?.ai.isIndexed),
    },
    {
      value: 'links',
      title: 'Links',
      description:
        metrics && metrics.links > 0
          ? `${metrics.links} saved link${metrics.links === 1 ? '' : 's'}`
          : 'Saved links & resource URLs',
      icon: LinkIcon,
      badge: metrics?.links && metrics.links > 0 ? metrics.links : undefined,
    },
    {
      value: 'docs',
      title: 'Docs',
      description: 'Reference documentation and guides',
      icon: BookOpen,
    },
    {
      value: 'files',
      title: 'Files',
      description:
        metrics && metrics.files > 0
          ? `${metrics.files} attached file${metrics.files === 1 ? '' : 's'}`
          : 'Uploaded attachments & media files',
      icon: Paperclip,
      badge: metrics?.files && metrics.files > 0 ? metrics.files : undefined,
    },
  ];

  const visibleFeatures = featuresWithMetrics.filter((feature) => {
    if (!flags) return true;
    if (feature.value === 'notes') {
      return (flags['anchor:notes'] ?? flags.notes) !== false;
    }
    if (feature.value === 'docs' || feature.value === 'files') {
      return (flags['anchor:files'] ?? flags.docs) !== false;
    }
    if (feature.value === 'threads') {
      return flags['anchor:comments'] !== false;
    }
    if (feature.value === 'chat') {
      return (flags.workflow_ai ?? flags.chat) !== false;
    }
    return true;
  });

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={cn(
          'flex items-center justify-between gap-0.5 w-full p-1 rounded-none border-x-0 border-y border-border/50 bg-background/90 shadow-2xs',
          className
        )}
      >
        {visibleFeatures.map((feature) => {
          const Icon = feature.icon;
          const isActive = activeFeature === feature.value;

          return (
            <Tooltip key={feature.value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onSelectFeature(feature.value)}
                  className={cn(
                    'relative flex-1 flex flex-col items-center justify-center py-1.5 px-1 rounded-lg transition-all text-xs font-medium cursor-pointer select-none outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    isActive
                      ? 'bg-primary/15 text-primary border border-primary/30 shadow-xs'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent'
                  )}
                  aria-label={feature.title}
                >
                  <Icon
                    className={cn(
                      'w-3.5 h-3.5 transition-transform duration-150',
                      isActive && 'scale-110'
                    )}
                  />
                  <span className="text-[9px] font-medium leading-tight mt-0.5 truncate max-w-full">
                    {feature.title}
                  </span>
                  {feature.badge !== undefined && (
                    <span
                      className={cn(
                        'absolute -top-1 -right-0.5 min-w-3.5 h-3.5 px-1 rounded-full text-[9px] font-bold flex items-center justify-center leading-none shadow-2xs pointer-events-none',
                        feature.hasAttention
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground border border-border/80'
                      )}
                    >
                      {feature.badge}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="center"
                sideOffset={6}
                className="!bg-popover !text-popover-foreground border border-border/80 shadow-lg max-w-[210px] text-center p-2.5 rounded-xl [&_svg]:!fill-popover [&_svg]:!bg-popover"
              >
                <p className="font-semibold text-xs text-popover-foreground">{feature.title}</p>
                <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                  {feature.description}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
